import { useState, type JSX } from "react";

import { Spinner } from "@/components/ui/spinner";

import { DocTypeEditor } from "./components/doc-type-editor";
import { DocTypeList } from "./components/doc-type-list";
import { useDocTypes } from "./hooks/use-doc-types";

type EditorMode = "create" | "edit";

export function DocTypeManagementPage(): JSX.Element {
  const { data: docTypes = [], isLoading, isError } = useDocTypes();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mode, setMode] = useState<EditorMode>("edit");

  if (isLoading) {
    return (
      <div className="flex min-h-64 items-center justify-center">
        <Spinner className="size-6" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex min-h-64 items-center justify-center p-8 text-center text-sm text-destructive">
        Unable to load document types. Please try again.
      </div>
    );
  }

  const selectedDocType =
    docTypes.find((docType) => docType.id === selectedId) ?? null;

  return (
    <div className="p-8">
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
        <section className="rounded-lg border p-4">
          <DocTypeList
            items={docTypes}
            selectedId={selectedId}
            onSelect={(id) => {
              setSelectedId(id);
              setMode("edit");
            }}
            onNew={() => {
              setSelectedId(null);
              setMode("create");
            }}
          />
        </section>

        <section className="min-w-0 rounded-lg border p-6">
          {mode === "create" || selectedDocType ? (
            <DocTypeEditor
              docType={selectedDocType}
              mode={mode}
              onSaved={(saved) => {
                setSelectedId(saved.id);
                setMode("edit");
              }}
              onDeleted={() => {
                setSelectedId(null);
                setMode("edit");
              }}
            />
          ) : (
            <div className="flex min-h-64 items-center justify-center text-center text-sm text-muted-foreground">
              Select a document type to edit, or create a new one.
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
